import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  InputAdornment, Link, MenuItem, Skeleton, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  appModalActionsSx, appModalCloseButtonSx, appModalFieldSx, appModalHeaderDividerSx, appModalPaperSx, appModalPrimaryButtonSx,
  appModalPrimaryLoadingSx, appModalSecondaryButtonSx, appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { leadsSelectMenuProps } from '../../Leads/components/leadsUi.styles';
import { colors } from '../../styles/theme';
import { createMetaCampaign, getMetaCreateOptions } from '../../services/metaAdsService';
import { formatMetaCountdown } from '../metaAdsConfig';
import { formatMetaMoney } from '../metaAdsMoney';
import {
  META_CREATE_DELETE_NOT_SYNCED_MESSAGE, META_CREATE_KEY_REUSED_MESSAGE, buildMetaCreateBody, metaAdsManagerUrl, metaCreateBlockedText, metaCreateBodyKey, metaCreateInitialForm, metaCreateNotice,
  metaCreatePartialText, metaCreatePartialView, metaCreateSuccessText, readMetaCreateDryRun, readMetaCreateError, readMetaCreateOptions, readMetaCreateResult,
  validateMetaCreateForm,
} from '../metaCreate';
import { clearMetaCreatePending, readMetaCreatePending, saveMetaCreatePending } from '../metaCreatePending';
import {
  META_CREATE_SHARE_ERROR, META_CREATE_SHARE_REASONS, metaCreateShareFix, metaCreateSharedMessage, shareMetaCreateRecord,
} from '../metaCreateShare';
import { offerMetaAutoUnshare } from '../metaShareGuard';
import { stripMetaTerrioMark } from '../metaFormat';
import { metaRecordNoun } from '../metaUnitCampaigns';
import { newMetaIdempotencyKey } from '../metaWrite';
import { useMetaCountdown } from '../hooks/useMetaCountdown';
import MetaWriteNote, { metaNoteButtonSx } from './MetaWriteNote';
import MetaWriteDialog from './MetaWriteDialog';
import { LinkMenu } from './MetaActionMenu';
import { toast } from '../../Components/Toast';
import { getScrollEdgeFadeHorizontalSx, getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { metaAdsSkeletonSx } from './metaAdsUi.styles';

/*
 * F2b API · «Crear campaña» desde la ficha (unidad o parcela del showroom). UN solo paso: arriba el
 * formulario pre-armado (`getmetacreateoptions`), abajo el resumen. «Revisar» corre el preview
 * (`dry_run`); con el preview vigente el botón pasa a «Crear en pausa» (sin doble confirmación, §9.3).
 * Si cambia cualquier campo, el preview se invalida y vuelve a «Revisar». Confirmar manda el MISMO
 * cuerpo + `expected` + `idempotency_key`. STALE → preview de nuevo; 429 → cuenta regresiva. Nunca
 * reintenta solo. Parcial: Reintentar / Eliminar lo creado (ver abajo); sin confirmar: aviso + Ads Manager.
 * El formulario (y su skeleton) tienen alto fijo. Los avisos cortos —compartir, no se puede
 * crear, error al cargar, listo, parcial— abrazan el texto.
 */

const DIALOG_HEIGHT = 'min(640px, 86vh)';
export const META_CREATE_DELETED_MESSAGE = 'Eliminaste lo creado en Meta.';
const fieldSx = {
  ...appModalFieldSx,
  '& .MuiInputLabel-root': { color: colors.textSecondary },
  '& .MuiInputLabel-root.Mui-focused': { color: colors.primaryLight },
  '& .MuiFormHelperText-root': { mx: 0, color: colors.textSecondary },
  '& .MuiFormHelperText-root.Mui-error': { color: colors.error },
  // Deshabilitado mientras corre un pedido: gris legible en el tema oscuro (MUI lo deja casi invisible).
  '& .MuiInputLabel-root.Mui-disabled': { color: colors.textSecondary },
  '& .MuiInputBase-input.Mui-disabled': { color: colors.disabledOnDarkText, WebkitTextFillColor: colors.disabledOnDarkText, opacity: 1 },
  '& .MuiOutlinedInput-root.Mui-disabled fieldset': { borderColor: colors.disabledOnDarkBorder },
  '& .MuiSelect-icon.Mui-disabled': { color: colors.disabledOnDarkText },
};
const multilineSx = { ...fieldSx, '& .MuiOutlinedInput-root': { ...appModalFieldSx['& .MuiOutlinedInput-root'], height: 'auto', alignItems: 'flex-start', py: 1 } };
const dateSx = { ...fieldSx, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.8)' } };
const rowSx = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 };
const sectionLabelSx = { fontSize: '12.5px', color: colors.textSecondary, mb: 0.75 };
/** Una o dos fotos: alto de la vista previa, al ancho del formulario. */
const PHOTO_PREVIEW_H = 168;
/** Tres o más: baldosas de la tira, más grandes que el recorte de 64 px. */
const PHOTO_TILE = 128;
// Parcial: «Eliminar lo creado» como botón de texto a la izquierda del footer, a la altura de Cerrar / Reintentar.
const textActionSx = {
  ...metaNoteButtonSx, fontSize: '14px', color: colors.textSecondary, mr: 'auto', height: 40, px: 0,
  '&:hover': { bgcolor: 'transparent', color: '#FBFBFB', textDecoration: 'underline' },
};
const partialMenuButtonSx = { width: 40, height: 40, minWidth: 40, minHeight: 40 };
const PARTIAL_MENU_ICONS = {
  'ads-manager': <OpenInNewIcon sx={{ fontSize: 20 }} />,
  discard: <ReplayIcon sx={{ fontSize: 20 }} />,
};

/**
 * Cuenta / Página con una sola opción: detalle (rótulo + valor), no un campo. No se elige:
 * viene de la cuenta y la página con las que se arma la campaña.
 */
function DetailReadout({ label, value, error, testId }) {
  const text = value || '—';
  return (
    <Box data-testid={testId} data-readonly="true" sx={{ minWidth: 0 }}>
      <Typography id={`${testId}-label`} sx={{ ...sectionLabelSx, mb: 0.5 }}>{label}</Typography>
      <Typography
        aria-labelledby={`${testId}-label`}
        title={value || ''}
        data-testid={`${testId}-value`}
        sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: '#FBFBFB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {text}
      </Typography>
      {error ? <Typography sx={{ mt: 0.5, fontSize: '12px', lineHeight: '16px', color: colors.error }}>{error}</Typography> : null}
    </Box>
  );
}

/** Cuenta / Página: una opción se lee como detalle; con varias, el selector. */
function ChoiceField({ label, value, options, onChange, disabled, error, testId }) {
  if (options.length < 2) {
    const only = options.find((o) => o.value === value) || options[0];
    return <DetailReadout label={label} value={only?.label || ''} error={error} testId={testId} />;
  }
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      size="small"
      error={Boolean(error)}
      helperText={error || ' '}
      SelectProps={{ MenuProps: leadsSelectMenuProps }}
      sx={fieldSx}
      data-testid={testId}
    >
      {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  );
}

/** Hoy (`YYYY-MM-DD`, hora local). */
function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function LoadingSkeleton() {
  const line = (h, w = '100%', mb = 1.5) => <Skeleton variant="rounded" height={h} sx={{ ...metaAdsSkeletonSx, width: w, mb }} />;
  const detail = (key) => (
    <Box key={key}>
      <Skeleton variant="rounded" height={12} sx={{ ...metaAdsSkeletonSx, width: '36%', mb: 0.75 }} />
      <Skeleton variant="rounded" height={16} sx={{ ...metaAdsSkeletonSx, width: '68%' }} />
    </Box>
  );
  return (
    <Box data-testid="meta-create-loading" aria-busy="true">
      <Box sx={{ ...rowSx, mb: 1.5 }}>{detail('account')}{detail('page')}</Box>
      {line(PHOTO_PREVIEW_H)}
      {line(40)}
      {line(40)}
      {line(84)}
      <Box sx={rowSx}>{line(40)}{line(40)}</Box>
      {line(40, '60%')}
      {line(56, '100%', 0)}
    </Box>
  );
}

/** Vista previa de la foto del anuncio. Una o dos ocupan el ancho del formulario; más, una tira. */
function PhotoStrip({ photos, value, onChange, disabled, error }) {
  const { scrollRef, fadeLeft, fadeRight } = useScrollEdgeFade({ axis: 'x' });
  const many = photos.length > 2;
  return (
    <Box>
      <Typography sx={sectionLabelSx} id="meta-create-photo-label">Foto</Typography>
      <Box
        ref={scrollRef}
        role="radiogroup"
        aria-labelledby="meta-create-photo-label"
        sx={{
          display: many ? 'flex' : 'grid',
          gridTemplateColumns: photos.length > 1 ? '1fr 1fr' : '1fr',
          gap: 1.5,
          ...(many ? {
            overflowX: 'auto',
            pb: 0.5,
            ...DOCK_SCROLLBAR_SX,
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            ...getScrollEdgeFadeHorizontalSx(fadeLeft, fadeRight),
          } : null),
        }}
      >
        {photos.map((p, i) => {
          const selected = p.id === value;
          return (
            <Box
              key={p.id}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Foto ${i + 1}${p.isCover ? ' (portada)' : ''}`}
              disabled={disabled}
              onClick={() => onChange(p.id)}
              data-testid={`meta-create-photo-${p.id}`}
              sx={{
                position: 'relative',
                flexShrink: 0,
                width: many ? PHOTO_TILE : '100%',
                height: many ? PHOTO_TILE : PHOTO_PREVIEW_H,
                p: 0,
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                bgcolor: 'rgba(255,255,255,0.04)',
                border: `2px solid ${selected ? colors.primaryLight : 'rgba(255,255,255,0.16)'}`,
                outline: 'none',
                '&:focus-visible': { boxShadow: `0 0 0 2px ${colors.primaryLight}` },
                '&:disabled': { cursor: 'default', opacity: 0.72 },
              }}
            >
              {p.url ? <Box component="img" src={p.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : null}
              {p.isCover ? (
                <Box component="span" sx={{ position: 'absolute', left: 8, bottom: 8, px: 0.75, borderRadius: '4px', fontSize: '11px', fontWeight: 600, lineHeight: '16px', color: '#FBFBFB', bgcolor: colors.primary }}>
                  Portada
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
      {error ? <Typography sx={{ fontSize: '12px', color: colors.error, mt: 0.5 }}>{error}</Typography> : null}
    </Box>
  );
}

const PART_VIEW = {
  created: { text: 'Creada', Icon: CheckCircleOutlineIcon, color: colors.primaryLight },
  failed: { text: 'Falló', Icon: HighlightOffIcon, color: colors.error },
  not_started: { text: 'No se creó', Icon: RemoveCircleOutlineIcon, color: colors.textSecondary },
  unknown: { text: 'Sin confirmar', Icon: HelpOutlineIcon, color: '#FFD2A8' },
};

/** Qué partes existen en Meta (✓ / ✗), en el orden en que se crean. */
function PartsList({ parts }) {
  if (!parts?.length) return null;
  return (
    <Box component="ul" data-testid="meta-create-parts" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {parts.map((part) => {
        const v = PART_VIEW[part.state] || PART_VIEW.unknown;
        const { Icon } = v;
        const statusColor = part.state === 'created' ? colors.textSecondary : v.color;
        return (
          <Box component="li" key={part.key} data-testid={`meta-create-part-${part.key}`} data-state={part.state} sx={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1, minHeight: 28, fontSize: '13px', lineHeight: '18px', color: '#FBFBFB' }}>
            <Icon sx={{ fontSize: 18, color: v.color }} aria-hidden />
            <Box component="span" sx={{ minWidth: 0 }}>{part.label}</Box>
            <Box component="span" sx={{ color: statusColor, textAlign: 'right' }}>{v.text}</Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * @param {{ open: boolean, itemKind: 'user_lot'|'cad_parcel', recordId: string, projectId?: string|null, onClose: () => void,
 *   onChanged?: (info: { kind: 'created'|'partial'|'unconfirmed'|'deleted', result?: object }) => void }} props
 *   `onChanged`: algo pudo quedar creado (o se eliminó) en Meta → la ficha y Campañas recargan.
 *   `projectId`: showroom de la parcela (para compartirlo); sin él se busca en `getmycadparcels`.
 *
 * Sin link público (`not_shared` / `share_restricted`, t1099u): un aviso que explica por qué y
 * «Compartir y seguir» / «Pasar a público y seguir» (`metaCreateShare`): comparte con el share de
 * siempre, vuelve a pedir las opciones y sigue con el formulario. Si falla, el error en el mismo aviso.
 *
 * T3 · Parcial (`META_CREATE_PARTIAL`, §5): queda guardado por registro (`metaCreatePending`) y el modal
 * muestra el estado (opción A: qué falta, «N de M pasos listos», un aviso, «Ver detalle» con las partes), «Reintentar» (MISMO cuerpo + `expected` + key: retoma desde el paso que falló),
 * «Eliminar lo creado» (el delete de F2a sobre la campaña) y, en el ⋮, «Ver en Ads Manager» / «Descartar este intento». Si el API ya no deja
 * retomar (key rechazada / reusada / sin confirmar), solo queda Eliminar: NUNCA se reintenta con una key
 * nueva habiendo una campaña (la duplicaría). Al abrir con un pendiente, arranca en ese estado.
 */
export default function MetaCreateCampaignDialog({ open, itemKind, recordId, projectId = null, onClose, onChanged = null }) {
  // loading | load_error | form | sharing | previewing | creating | done | partial | retrying
  const [stage, setStage] = useState('loading');
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [dry, setDry] = useState(null); // { key, body, preview, expected }
  const [error, setError] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [result, setResult] = useState(null);
  // Parcial vigente: la entrada de `metaCreatePending` + `message` + `retryable`.
  const [partial, setPartial] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [partsOpen, setPartsOpen] = useState(false);
  const keyRef = useRef(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const countdown = useMetaCountdown();
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const { start: startCountdown, reset: resetCountdown } = countdown;
  const today = useMemo(() => todayIso(), []);
  const noun = metaRecordNoun(itemKind);
  const record = useMemo(() => ({ itemKind, id: recordId }), [itemKind, recordId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; abortRef.current?.abort(); };
  }, []);

  const load = useCallback(async (adAccountId = null, prevForm = null) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStage('loading');
    setError(null);
    setDry(null);
    let res;
    try {
      res = await getMetaCreateOptions({ itemKind, id: recordId, adAccountId, signal: controller.signal });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      throw e;
    }
    if (!mountedRef.current || controller.signal.aborted) return;
    if (!res?._httpOk) {
      setError(readMetaCreateError(res));
      setStage('load_error');
      return;
    }
    const next = readMetaCreateOptions(res);
    setOptions(next);
    setForm(metaCreateInitialForm(next, prevForm));
    setFieldErrors({});
    setStage('form');
  }, [itemKind, recordId]);

  useEffect(() => {
    if (!open) return;
    setOptions(null);
    setForm(null);
    setResult(null);
    setAdvanced(false);
    setPartial(null);
    setDeleteTarget(null);
    setPartsOpen(false);
    setError(null);
    resetCountdown();
    keyRef.current = null;
    // Un parcial de antes en este registro: se retoma ahí (Reintentar / Eliminar), sin formulario.
    const pending = readMetaCreatePending(record);
    if (pending) {
      setPartial({
        ...pending,
        message: pending.retryable ? metaCreatePartialText(pending, noun) : META_CREATE_KEY_REUSED_MESSAGE,
      });
      setStage('partial');
      return;
    }
    load();
    // Solo al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemKind, recordId]);

  const body = useMemo(() => (form ? buildMetaCreateBody({ itemKind, id: recordId }, form) : null), [form, itemKind, recordId]);
  const bodyKey = metaCreateBodyKey(body);
  // El preview vale solo para el cuerpo con el que se pidió: cualquier cambio vuelve a «Revisar».
  const dryValid = Boolean(dry?.expected && dry.key === bodyKey);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
    if (error && error.kind !== 'rate_limited') setError(null);
  };

  const changeAccount = (adAccountId) => {
    if (!adAccountId || adAccountId === form?.adAccountId) return;
    load(adAccountId, form);
  };

  const handleError = useCallback((err) => {
    // El link se cortó entre las opciones y el preview: mismo aviso con «Compartir y seguir».
    if (err.kind === 'blocked' && META_CREATE_SHARE_REASONS.includes(err.reason)) {
      setOptions((prev) => (prev ? { ...prev, canCreate: false, blockedReason: err.reason } : prev));
      setDry(null);
      setError(null);
      return;
    }
    if (err.kind === 'field') {
      setFieldErrors((prev) => ({ ...prev, [err.field]: err.message }));
      if (err.field === 'radiusKm' || err.field === 'ageMin') setAdvanced(true);
      setError(null);
      return;
    }
    setError(err);
    if (err.kind === 'rate_limited') startCountdown(err.retryAfterS);
  }, [startCountdown]);

  const runPreview = useCallback(async ({ keepError = null } = {}) => {
    const errors = validateMetaCreateForm(form, options?.limits, today);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      if (errors.radiusKm || errors.ageMin) setAdvanced(true);
      return;
    }
    const sent = body;
    const sentKey = bodyKey;
    setStage('previewing');
    setError(keepError);
    resetCountdown();
    const res = await createMetaCampaign({ body: sent, dryRun: true });
    if (!mountedRef.current) return;
    setStage('form');
    if (!res?._httpOk) {
      setDry(null);
      handleError(readMetaCreateError(res, form?.currency));
      return;
    }
    const parsed = readMetaCreateDryRun(res);
    setDry({ ...parsed, key: sentKey, body: sent });
    keyRef.current = newMetaIdempotencyKey();
  }, [form, options, today, body, bodyKey, handleError, resetCountdown]);

  const confirm = async () => {
    if (!dryValid) return;
    setStage('creating');
    setError(null);
    if (!keyRef.current) keyRef.current = newMetaIdempotencyKey();
    const res = await createMetaCampaign({ body: dry.body, expected: dry.expected, idempotencyKey: keyRef.current });
    if (!mountedRef.current) return;
    if (res?._httpOk) {
      const done = readMetaCreateResult(res);
      setResult(done);
      setStage('done');
      onChanged?.({ kind: 'created', result: done });
      return;
    }
    const err = readMetaCreateError(res, form?.currency, noun);
    setStage('form');
    if (err.kind === 'stale') {
      // Cambió la ficha o la cuenta: preview de nuevo (key nuevo), con el aviso.
      keyRef.current = null;
      setDry(null);
      runPreview({ keepError: { ...err, kind: 'stale' } });
      return;
    }
    const lockedWithCampaign = Boolean(err.campaignId) && ['key_reused', 'key_rejected'].includes(err.kind);
    if (err.kind === 'key_reused' && !lockedWithCampaign) {
      keyRef.current = null;
      setDry(null);
      runPreview({ keepError: { ...err, kind: 'info', message: 'Revisá y confirmá de nuevo.' } });
      return;
    }
    if (err.kind === 'partial' || lockedWithCampaign) {
      // Con campaña creada NUNCA se pide key nueva (la duplicaría). Si el API ya no deja retomar: solo Eliminar.
      enterPartial({
        createId: err.createId,
        body: dry.body,
        expected: dry.expected,
        idempotencyKey: keyRef.current,
        campaignId: err.campaignId,
        parts: err.parts,
        failedStep: err.failedStep,
        causeCode: err.causeCode,
        metaUrl: err.metaUrl,
        retryable: err.kind === 'partial',
        savedAt: Date.now(),
      }, err);
      onChanged?.({ kind: 'partial' });
      return;
    }
    if (err.kind === 'unconfirmed') {
      // No se sabe qué quedó: «Revisalo en Meta», sin reintentar.
      setError(err);
      onChanged?.({ kind: 'unconfirmed' });
      return;
    }
    if (err.createId && !err.campaignId && err.kind !== 'in_progress') {
      // Falló antes de la campaña (no quedó nada visible): key nueva y preview nuevo.
      keyRef.current = null;
      setDry(null);
      handleError(err);
      return;
    }
    // Red caída o la misma creación en curso: el key se conserva (si ya se creó, vuelve `replayed`).
    if (err.kind !== 'network' && err.kind !== 'in_progress') keyRef.current = newMetaIdempotencyKey();
    if (err.kind === 'field') setDry(null);
    handleError(err);
  };

  /** Pasa al estado parcial y lo guarda por registro (si se puede guardar). */
  function enterPartial(entry, err = null) {
    saveMetaCreatePending(record, entry);
    setPartial({ ...entry, message: err?.message || metaCreatePartialText(entry, noun) });
    setError(null);
    setStage('partial');
    if (err?.retryAfterS) startCountdown(err.retryAfterS);
  }

  /** Ya no se puede retomar: queda solo Eliminar lo creado (y Ver en Ads Manager). */
  function blockPartial(message, metaUrl = null) {
    const next = { ...partial, retryable: false, metaUrl: metaUrl || partial.metaUrl };
    saveMetaCreatePending(record, next);
    setPartial({ ...next, message });
    setError(null);
    setStage('partial');
  }

  const retry = async () => {
    if (!partial?.retryable || stage === 'retrying') return;
    setStage('retrying');
    setError(null);
    // MISMO cuerpo, MISMO expected y MISMA key: el API retoma desde el paso que falló.
    const res = await createMetaCampaign({ body: partial.body, expected: partial.expected, idempotencyKey: partial.idempotencyKey });
    if (!mountedRef.current) return;
    if (res?._httpOk) {
      const done = readMetaCreateResult(res);
      clearMetaCreatePending(record);
      setPartial(null);
      setResult(done);
      setStage('done');
      onChanged?.({ kind: 'created', result: done });
      return;
    }
    const err = readMetaCreateError(res, partial.body?.currency, noun);
    if (err.kind === 'partial') {
      enterPartial({
        ...partial,
        createId: err.createId || partial.createId,
        campaignId: err.campaignId || partial.campaignId,
        parts: err.parts.length ? err.parts : partial.parts,
        failedStep: err.failedStep || partial.failedStep,
        causeCode: err.causeCode || partial.causeCode,
        metaUrl: err.metaUrl || partial.metaUrl,
        retryable: true,
      }, err);
      onChanged?.({ kind: 'partial' });
      return;
    }
    if (err.kind === 'key_rejected' || err.kind === 'key_reused' || err.kind === 'stale') {
      blockPartial(err.kind === 'key_rejected' ? err.message : META_CREATE_KEY_REUSED_MESSAGE);
      return;
    }
    if (err.kind === 'unconfirmed') {
      blockPartial(err.message, err.metaUrl);
      onChanged?.({ kind: 'unconfirmed' });
      return;
    }
    // Red, en curso, límite de Meta u otro: se puede volver a reintentar con la MISMA key.
    setStage('partial');
    setError(err);
    if (err.retryAfterS) startCountdown(err.retryAfterS);
  };

  const askDelete = () => {
    if (!partial?.campaignId) return;
    setDeleteTarget({
      level: 'campaign', action: 'delete', id: partial.campaignId, name: stripMetaTerrioMark(partial.body?.name || '') || 'la campaña',
    });
  };
  const handleDeleted = () => {
    const deletedId = partial?.campaignId;
    clearMetaCreatePending(record);
    setDeleteTarget(null);
    setPartial(null);
    setError(null);
    resetCountdown();
    // Listo: el modal de creación se cierra y avisa el toast (como las demás acciones de F2a).
    toast.success({ message: META_CREATE_DELETED_MESSAGE });
    // Paso 3: si el share lo creó «Crear campaña» y ya no lo usa otra, el toast ofrece dejar de compartir.
    offerMetaAutoUnshare({ records: [{ itemKind, id: recordId, projectId }], deletedCampaignId: deletedId, baseMessage: META_CREATE_DELETED_MESSAGE });
    onChanged?.({ kind: 'deleted' });
    onClose?.();
  };
  const discard = () => {
    clearMetaCreatePending(record);
    setPartial(null);
    resetCountdown();
    keyRef.current = null;
    load();
  };

  const blockedReason = options && !options.canCreate ? options.blockedReason : null;
  const shareFix = stage !== 'load_error' ? metaCreateShareFix(blockedReason, itemKind) : null;
  const runShare = async () => {
    if (!shareFix || stage === 'sharing') return;
    setStage('sharing');
    setError(null);
    const out = await shareMetaCreateRecord({ itemKind, recordId, reason: shareFix.reason, projectId });
    if (!mountedRef.current) return;
    if (!out.ok) {
      setStage('form');
      setError({ kind: 'share', message: out.message || META_CREATE_SHARE_ERROR });
      return;
    }
    toast.success({ message: metaCreateSharedMessage(shareFix.reason, itemKind) });
    load(form?.adAccountId || null, form);
  };

  if (!open) return null;

  const busy = stage === 'loading' || stage === 'sharing' || stage === 'previewing' || stage === 'creating' || stage === 'retrying';
  const inPartial = stage === 'partial' || stage === 'retrying';
  const waiting = countdown.left > 0;
  const final = error && ['partial', 'unconfirmed', 'owner_only', 'disabled', 'blocked', 'unavailable', 'permission', 'not_enabled'].includes(error.kind);
  const canCreate = stage !== 'load_error' && options?.canCreate === true;
  // El formulario no salta de alto. Un aviso corto (compartir, bloqueo, error, listo, parcial) sí: abraza el texto.
  const tall = stage === 'loading' || ((stage === 'form' || stage === 'previewing' || stage === 'creating') && canCreate);
  const blockedText = options && !options.canCreate ? metaCreateBlockedText(options.blockedReason) : null;
  const limits = options?.limits || {};
  const accounts = options?.accounts || [];
  const pages = options?.pages || [];
  const currency = form?.currency || null;

  const openMeta = (url, testId = 'meta-create-open-ads-manager', label = 'Ver en Ads Manager') => (url ? (
    <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ ...metaNoteButtonSx, display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid={testId}>
      {label} <OpenInNewIcon sx={{ fontSize: 13 }} aria-hidden />
    </Link>
  ) : null);
  // Parcial (opción A): estado + resumen, un aviso compacto y el detalle de pasos plegado.
  const partialView = inPartial && partial ? metaCreatePartialView(partial, noun) : null;
  const partialMetaUrl = partial ? (partial.metaUrl || metaAdsManagerUrl(partial.body?.ad_account_id)) : null;

  // UN solo aviso: el error (si hay) manda; si no, los avisos del preview o, antes, los de la cuenta.
  let note = null;
  if (error && error.kind === 'share') {
    // El error de compartir va en el mismo aviso del bloqueo (uno solo).
  } else if (error) {
    if (error.kind === 'rate_limited') {
      note = (
        <MetaWriteNote tone="warn" testId="meta-create-rate-limited">
          <strong>{error.message}</strong>
          {waiting ? ` · Probá en ${formatMetaCountdown(countdown.left)}` : ' · Ya podés probar de nuevo'}
        </MetaWriteNote>
      );
    } else if (error.kind === 'stale' || error.kind === 'info') {
      note = <MetaWriteNote tone="warn" testId={`meta-create-${error.kind}`}>{error.message}</MetaWriteNote>;
    } else if (error.kind === 'in_progress') {
      note = <MetaWriteNote tone="info" testId="meta-create-in-progress">{error.message}</MetaWriteNote>;
    } else {
      const tone = ['partial', 'unconfirmed', 'payment', 'verification'].includes(error.kind) ? 'warn' : 'error';
      note = <MetaWriteNote tone={tone} action={openMeta(error.metaUrl)} testId={`meta-create-error-${error.kind}`}>{error.message}</MetaWriteNote>;
    }
  } else if (inPartial && partial) {
    note = (
      <MetaWriteNote tone={partialView.tone} action={openMeta(partialMetaUrl, 'meta-create-partial-link', partialView.linkLabel)} testId="meta-create-partial-note">
        {partialView.cause}
      </MetaWriteNote>
    );
  } else if (stage === 'form' || stage === 'previewing' || stage === 'creating') {
    const notice = metaCreateNotice(dryValid ? dry.preview.warnings : options?.warnings);
    if (notice) note = <MetaWriteNote tone={notice.tone} testId="meta-create-notice" codes={notice.codes}>{notice.text}</MetaWriteNote>;
  }

  let content;
  let partsDetail = null;
  if (inPartial && partial) {
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="meta-create-partial">
        <Typography sx={{ fontSize: '12.5px', color: colors.textSecondary, lineHeight: '18px', overflowWrap: 'anywhere' }} data-testid="meta-create-partial-name">
          {stripMetaTerrioMark(partial.body?.name || '') || 'Campaña'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <DonutLargeIcon sx={{ fontSize: 20, color: colors.primaryLight, flexShrink: 0 }} aria-hidden />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" sx={{ fontSize: '15px', fontWeight: 600, color: '#FBFBFB', lineHeight: '20px' }} data-testid="meta-create-partial-title">
              {partialView.title}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: colors.textSecondary, lineHeight: '18px' }} data-testid="meta-create-partial-paused">
              {partialView.progress}
            </Typography>
          </Box>
        </Box>
        {partial.retryable && waiting ? (
          <Typography sx={{ fontSize: '12.5px', color: colors.textSecondary }} data-testid="meta-create-retry-countdown">
            {`Podés reintentar en ${formatMetaCountdown(countdown.left)}.`}
          </Typography>
        ) : null}
      </Box>
    );
    partsDetail = partial.parts?.length ? (
      <Box>
        <Button
          size="small"
          onClick={() => setPartsOpen((v) => !v)}
          aria-expanded={partsOpen}
          endIcon={<ExpandMoreIcon sx={{ transform: partsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
          sx={{
            ...metaNoteButtonSx,
            color: colors.textSecondary,
            width: '100%',
            justifyContent: 'space-between',
            minHeight: 32,
            '&:hover': { bgcolor: 'transparent', color: '#FBFBFB' },
          }}
          data-testid="meta-create-partial-detail"
        >
          Ver detalle
        </Button>
        <Collapse in={partsOpen} unmountOnExit>
          <Box sx={{ pt: 0.5 }}><PartsList parts={partial.parts} /></Box>
        </Collapse>
      </Box>
    ) : null;
  } else if (stage === 'loading') {
    content = <LoadingSkeleton />;
  } else if (stage === 'load_error') {
    content = null;
  } else if (stage === 'done') {
    const linkedText = metaCreateSuccessText(result, noun);
    content = (
      <MetaWriteNote tone="info" testId="meta-create-done" action={openMeta(result?.metaUrl)}>
        <strong>{linkedText}</strong>
        {' No gasta hasta que la actives.'}
        {result && !result.refreshed && result.refreshNote ? <Box sx={{ mt: 0.5 }}>{result.refreshNote}</Box> : null}
      </MetaWriteNote>
    );
  } else if (!canCreate && shareFix) {
    const failed = error?.kind === 'share';
    content = (
      <MetaWriteNote tone={failed ? 'error' : 'info'} testId="meta-create-share-fix">
        {failed ? error.message : shareFix.text}
      </MetaWriteNote>
    );
  } else if (!canCreate) {
    content = <MetaWriteNote tone="info" testId="meta-create-blocked">{blockedText || 'No se puede crear la campaña.'}</MetaWriteNote>;
  } else {
    const disabled = busy || final;
    const fe = fieldErrors;
    const hMax = limits.headlineMax ?? 100;
    const mMax = limits.messageMax ?? 1000;
    const budgetHelper = [
      limits.dailyBudgetMin != null ? `Mín. ${formatMetaMoney(limits.dailyBudgetMin, currency)}` : null,
      limits.dailyBudgetMax != null ? `Máx. ${formatMetaMoney(limits.dailyBudgetMax, currency)}` : null,
    ].filter(Boolean).join(' · ');
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="meta-create-form">
        <Box sx={{ ...rowSx, alignItems: 'start' }}>
          <ChoiceField
            label="Cuenta"
            value={form.adAccountId}
            options={accounts.map((a) => ({ value: a.id, label: a.label }))}
            onChange={changeAccount}
            disabled={disabled}
            error={fe.adAccountId}
            testId="meta-create-account"
          />
          <ChoiceField
            label="Página"
            value={form.pageId}
            options={pages.map((p) => ({ value: p.id, label: p.name || p.id }))}
            onChange={(v) => setField('pageId', v)}
            disabled={disabled}
            error={fe.pageId}
            testId="meta-create-page"
          />
        </Box>
        <PhotoStrip photos={options.photos} value={form.imageId} onChange={(id) => setField('imageId', id)} disabled={disabled} error={fe.imageId} />
        <TextField
          label="Nombre de la campaña"
          value={form.name}
          onChange={(e) => setField('name', e.target.value.replace(/[\r\n]+/g, ' '))}
          disabled={disabled}
          size="small"
          fullWidth
          error={Boolean(fe.name)}
          helperText={fe.name || 'Solo lo ves vos en Meta.'}
          inputProps={{ maxLength: 420, 'data-testid': 'meta-create-name' }}
          sx={fieldSx}
        />
        <TextField
          label="Título"
          value={form.headline}
          onChange={(e) => setField('headline', e.target.value.replace(/[\r\n]+/g, ' '))}
          disabled={disabled}
          size="small"
          fullWidth
          error={Boolean(fe.headline)}
          helperText={fe.headline || `${form.headline.trim().length}/${hMax}`}
          inputProps={{ maxLength: hMax + 20, 'data-testid': 'meta-create-headline' }}
          sx={fieldSx}
        />
        <TextField
          label="Texto"
          value={form.message}
          onChange={(e) => setField('message', e.target.value)}
          disabled={disabled}
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          error={Boolean(fe.message)}
          helperText={fe.message || `${form.message.trim().length}/${mMax}`}
          inputProps={{ maxLength: mMax + 50, 'data-testid': 'meta-create-message' }}
          sx={multilineSx}
        />
        <TextField
          label="Presupuesto diario"
          value={form.dailyBudget}
          onChange={(e) => setField('dailyBudget', e.target.value)}
          disabled={disabled}
          size="small"
          fullWidth
          error={Boolean(fe.dailyBudget)}
          helperText={fe.dailyBudget || budgetHelper || ' '}
          InputProps={{
            startAdornment: currency ? <InputAdornment position="start"><Box component="span" sx={{ color: colors.textSecondary, fontSize: '13px' }}>{currency}</Box></InputAdornment> : null,
          }}
          inputProps={{ inputMode: 'decimal', maxLength: 20, 'data-testid': 'meta-create-budget' }}
          sx={fieldSx}
        />
        <Box sx={rowSx}>
          <TextField
            type="date"
            label="Empieza (opcional)"
            value={form.startDate}
            onChange={(e) => setField('startDate', e.target.value)}
            disabled={disabled}
            size="small"
            error={Boolean(fe.startDate)}
            helperText={fe.startDate || 'Si no, al activarla.'}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today, 'data-testid': 'meta-create-start' }}
            sx={dateSx}
          />
          <TextField
            type="date"
            label="Termina (opcional)"
            value={form.endDate}
            onChange={(e) => setField('endDate', e.target.value)}
            disabled={disabled}
            size="small"
            error={Boolean(fe.endDate)}
            helperText={fe.endDate || ' '}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: form.startDate || today, 'data-testid': 'meta-create-end' }}
            sx={dateSx}
          />
        </Box>
        <Box>
          <Button
            size="small"
            onClick={() => setAdvanced((v) => !v)}
            aria-expanded={advanced}
            endIcon={<ExpandMoreIcon sx={{ transform: advanced ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
            sx={{ ...metaNoteButtonSx, fontSize: '13px' }}
            data-testid="meta-create-advanced"
          >
            Opciones avanzadas
          </Button>
          <Collapse in={advanced}>
            <Box sx={{ ...rowSx, mt: 1.5 }}>
              <TextField
                label="Radio"
                value={form.radiusKm}
                onChange={(e) => setField('radiusKm', e.target.value)}
                disabled={disabled}
                size="small"
                error={Boolean(fe.radiusKm)}
                helperText={fe.radiusKm || 'Alrededor del pin.'}
                InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ color: colors.textSecondary, fontSize: '13px' }}>km</Box></InputAdornment> }}
                inputProps={{ inputMode: 'numeric', maxLength: 3, 'data-testid': 'meta-create-radius' }}
                sx={fieldSx}
              />
              <TextField
                label="Edad mínima"
                value={form.ageMin}
                onChange={(e) => setField('ageMin', e.target.value)}
                disabled={disabled}
                size="small"
                error={Boolean(fe.ageMin)}
                helperText={fe.ageMin || ' '}
                InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ color: colors.textSecondary, fontSize: '13px' }}>años</Box></InputAdornment> }}
                inputProps={{ inputMode: 'numeric', maxLength: 2, 'data-testid': 'meta-create-age' }}
                sx={fieldSx}
              />
            </Box>
          </Collapse>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box data-testid="meta-create-summary" sx={{ minHeight: 56 }}>
          {dryValid ? (
            <>
              {dry.preview.text ? <Typography sx={{ fontSize: '13px', color: '#FBFBFB', lineHeight: '18px' }}>{dry.preview.text}</Typography> : null}
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#CFE6E5', mt: 0.5 }} data-testid="meta-create-paused">
                Se crea en pausa: no gasta hasta que la actives.
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: '13px', color: colors.textSecondary, lineHeight: '18px' }}>
              {stage === 'previewing' ? 'Revisando…' : 'Tocá «Revisar» para ver el resumen antes de crear.'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  let showPrimary = stage !== 'done' && !final && (canCreate || stage === 'load_error' || Boolean(shareFix));
  let primaryLabel = dryValid ? 'Crear en pausa' : 'Revisar';
  if (stage === 'load_error') primaryLabel = 'Reintentar';
  else if (!canCreate && shareFix) primaryLabel = shareFix.actionLabel;
  if (inPartial) {
    showPrimary = Boolean(partial?.retryable);
    primaryLabel = 'Reintentar';
  }
  const onPrimary = () => {
    if (inPartial) retry();
    else if (stage === 'load_error') load(form?.adAccountId || null, form);
    else if (!canCreate && shareFix) runShare();
    else if (dryValid) confirm();
    else runPreview();
  };
  const loading = stage === 'sharing' || stage === 'previewing' || stage === 'creating' || stage === 'retrying';
  const primarySx = loading ? { ...appModalPrimaryButtonSx, ...appModalPrimaryLoadingSx } : appModalPrimaryButtonSx;
  const title = inPartial ? null : options?.record?.title;

  return (
    <>
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...appModalPaperSx,
          width: 'min(520px, 95vw)',
          maxWidth: '520px',
          height: tall ? DIALOG_HEIGHT : 'auto',
          maxHeight: DIALOG_HEIGHT,
        },
        'data-testid': 'meta-create-dialog',
        'data-layout': tall ? 'form' : 'compact',
      }}
      aria-labelledby="meta-create-title"
    >
      <DialogTitle id="meta-create-title" component="div" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>Crear campaña</Typography>
        <IconButton size="small" onClick={onClose} disabled={stage === 'creating' || stage === 'retrying'} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent ref={scrollRef} sx={{ ...DOCK_SCROLLBAR_SX, px: '20px', pt: 2, pb: tall ? 1 : 2, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: tall ? 1 : '1 1 auto', minHeight: 0, ...getScrollEdgeFadeSx(fadeTop, fadeBottom) }}>
        {title && stage !== 'loading' ? (
          <Typography sx={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere', color: '#FBFBFB' }} data-testid="meta-create-record">{title}</Typography>
        ) : null}
        {content}
        {note}
        {partsDetail}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        {inPartial && partial?.campaignId && showPrimary ? (
          <Button variant="text" onClick={askDelete} disabled={busy} sx={textActionSx} data-testid="meta-create-delete">
            Eliminar lo creado
          </Button>
        ) : null}
        {inPartial && partial ? (
          <Box sx={{ display: 'inline-flex', ...(partial.campaignId && showPrimary ? {} : { mr: 'auto' }) }}>
            <LinkMenu
              label="Más opciones"
              testId="meta-create-more"
              disabled={busy}
              buttonSx={partialMenuButtonSx}
              icons={PARTIAL_MENU_ICONS}
              items={[
                partialMetaUrl ? { key: 'ads-manager', label: 'Ver en Ads Manager', onClick: () => window.open(partialMetaUrl, '_blank', 'noopener,noreferrer') } : null,
                { key: 'discard', label: 'Descartar este intento', onClick: discard },
              ]}
            />
          </Box>
        ) : null}
        <Button variant="outlined" onClick={onClose} disabled={stage === 'creating' || stage === 'retrying'} sx={appModalSecondaryButtonSx}>
          {showPrimary && !inPartial ? 'Cancelar' : 'Cerrar'}
        </Button>
        {inPartial && partial?.campaignId && !showPrimary ? (
          // Ya no se puede reintentar: Eliminar lo creado pasa a ser el principal.
          <Button variant="contained" onClick={askDelete} disabled={busy} sx={appModalPrimaryButtonSx} data-testid="meta-create-delete">
            Eliminar lo creado
          </Button>
        ) : null}
        {showPrimary ? (
          <Button
            variant="contained"
            onClick={onPrimary}
            disabled={busy || waiting}
            sx={primarySx}
            startIcon={loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            data-testid="meta-create-primary"
          >
            {primaryLabel}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
    {deleteTarget ? (
      <MetaWriteDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onApplied={handleDeleted}
        onStale={() => onChanged?.({ kind: 'partial' })}
        notFound={{ message: META_CREATE_DELETE_NOT_SYNCED_MESSAGE, metaUrl: partial?.metaUrl || metaAdsManagerUrl(partial?.body?.ad_account_id) }}
      />
    ) : null}
    </>
  );
}
